/**
 * SupportSessionBanner.js (Lane 1416)
 *
 * Shown on every page while the browser holds a Wingman support-session token
 * (the admin opened a customer's workspace from the admin portal). Fixed in
 * the middle of the 60px top header bar (AppLayout), so it never moves the
 * page layout or covers the chat buttons. "End session" ends the
 * session on the server (the token stops working on the next request), signs
 * out and returns to the admin login. If the server does not confirm the end,
 * the session stays as it is and the banner says so, so it can be retried
 * (signing out locally would leave the token working elsewhere).
 */

import React, { useState } from 'react';
import { useAuth } from '../AuthContext';
import { API_BASE, LAYOUT_TOP_OFFSET } from '../config';

function SupportSessionBanner() {
  const { user, token, logout } = useAuth();
  const [ending, setEnding] = useState(false);
  const [failed, setFailed] = useState(false);
  if (!user || user.user_type !== 'company' || !user.support_session) return null;

  const endSession = async () => {
    setEnding(true);
    setFailed(false);
    let ended = false;
    try {
      const response = await fetch(`${API_BASE}/api/support-session/end`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      // 401: the session had already ended or expired on the server.
      ended = response.ok || response.status === 401;
    } catch (err) {
      ended = false;
    }
    if (!ended) {
      setEnding(false);
      setFailed(true);
      return;
    }
    logout();
    window.location.assign('/admin/login');
  };

  return (
    <div
      role="status"
      style={{
        position: 'fixed',
        top: `${LAYOUT_TOP_OFFSET + 12}px`,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 10000,
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '4px 4px 4px 14px',
        height: '36px',
        boxSizing: 'border-box',
        background: '#C2410C',
        color: '#FFFFFF',
        borderRadius: '999px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
        fontSize: '0.875rem',
        fontWeight: 600,
        whiteSpace: 'nowrap',
      }}
    >
      <span>
        {failed
          ? 'Could not end the session. Try again.'
          : `Wingman support session in ${user.company_name}`}
      </span>
      <button
        type="button"
        onClick={endSession}
        disabled={ending}
        style={{
          padding: '4px 12px',
          border: 'none',
          borderRadius: '999px',
          background: '#FFFFFF',
          color: '#C2410C',
          fontWeight: 600,
          fontSize: '0.875rem',
          cursor: ending ? 'not-allowed' : 'pointer',
        }}
      >
        {ending ? 'Ending…' : (failed ? 'Try again' : 'End session')}
      </button>
    </div>
  );
}

export default SupportSessionBanner;
